import { createContext, useContext, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import type { Member } from '../../lib/supabase'
import { useLayout } from '../../hooks/useLayout'
import { Wordmark } from '../Wordmark'
import { ChartIcon, GearIcon } from '../icons'
import { useTabSwipe } from '../../hooks/useTabSwipe'
import { stripX } from '../../lib/tabSwipe'
import { Avatars } from './Avatars'

// The app's frame (feature spec 01 Scope): three tabs, List · Recipes · Plan,
// and the one way into Settings. Phone: Matlist written out with Stats and the
// people, then four small tabs — Home · List · Recipes · Plan — all always in
// view, a lime bar under the open one (no counts: the hero says them). Swiping
// the page sideways changes tab (hooks/useTabSwipe.ts) and the bar slides
// along; a tap on a tab does too. Home (components/home) is phone only. While Get started has steps left, its ring goes round Stats' button and
// the tap opens both (App). Desktop: the tabs at the top of
// the sidebar, the open tab's own sidebar content (SidebarSlot) below them,
// and Settings at the foot. Stats sits beside the people: a round button left
// of the pill on the phone, a row above Settings on desktop. "Plan" is the meal planner; the code calls it
// `meals`, since plan already means the billing plan and the desktop layout.

export type Tab = 'home' | 'list' | 'recipes' | 'meals'

/** The desktop sidebar's three; the phone puts Home before them. */
const TABS: { value: Tab; label: string }[] = [
  { value: 'list', label: 'List' },
  { value: 'recipes', label: 'Recipes' },
  { value: 'meals', label: 'Plan' },
]
const PHONE_TABS: { value: Tab; label: string }[] = [{ value: 'home', label: 'Home' }, ...TABS]

interface ShellContext {
  slot: HTMLElement | null
}
const Ctx = createContext<ShellContext | null>(null)

/** What the open tab shows in the desktop sidebar under the tabs. Nothing on the phone. */
export function SidebarSlot({ children }: { children: ReactNode }) {
  const ctx = useContext(Ctx)
  return ctx?.slot ? createPortal(children, ctx.slot) : null
}

/** The avatars + gear pill. On the phone's title row it is compact — no gear, two
 *  circles at most (a pair, else one and "+n") — to leave the tabs room. */
function SettingsPill({ members, onClick, compact = false }: { members: Member[]; onClick: () => void; compact?: boolean }) {
  const gear = !compact
  return (
    <button type="button" className="ml-settings-pill" aria-label="Settings, household and family" onClick={onClick} data-tour="people">
      <Avatars members={members} max={compact ? (members.length > 2 ? 1 : 2) : undefined} />
      {gear && <GearIcon size={20} />}
    </button>
  )
}

export function AppShell({ tab, onTab, counts, members, onOpenSettings, onOpenStats, start, children }: {
  tab: Tab
  onTab: (t: Tab) => void
  counts: Partial<Record<Tab, number>>
  members: Member[]
  onOpenSettings: () => void
  /** The stats page; no button without it. */
  onOpenStats?: () => void
  /** Get started (lib/getStarted.ts): steps done of total; no button once it's done or hidden. */
  start?: { done: number; total: number; onOpen: () => void }
  children: ReactNode
}) {
  const layout = useLayout()
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  const ids = useId()
  const options = TABS.map(t => ({ ...t, count: counts[t.value] }))
  const ctx: ShellContext = { slot: layout === 'desktop' ? slot : null }

  if (layout === 'phone') {
    return (
      <Ctx.Provider value={ctx}>
        <PhoneShell tab={tab} onTab={onTab} members={members} onOpenSettings={onOpenSettings}
          onOpenStats={onOpenStats} start={start} panelId={`${ids}-panel`}>{children}</PhoneShell>
      </Ctx.Provider>
    )
  }

  return (
    <Ctx.Provider value={ctx}>
      <div className="ml-shell">
        <aside className="ml-sidebar">
          <Wordmark size={22} />
          <nav className="ml-nav ml-shell__tabs" aria-label="Sections">
            {options.map(o => (
              <button key={o.value} type="button" aria-current={o.value === tab ? 'page' : undefined} data-tour={`tab-${o.value}`}
                className={`ml-nav__row${o.value === tab ? ' is-selected' : ''}`} onClick={() => onTab(o.value)}>
                <span className="ml-nav__label">{o.label}</span>
                {o.count != null && <>{' '}<span className="ml-nav__count ml-num">{o.count}</span></>}
              </button>
            ))}
          </nav>
          <div className="ml-shell__slot" ref={setSlot} />
          {start && (
            <button type="button" className="ml-shell__settings ml-shell__start" onClick={start.onOpen} data-tour="get-started">
              <StartRing done={start.done} total={start.total} size={20} />
              <span className="ml-shell__settings-label">Get started</span>
              <span className="ml-nav__count ml-num">{start.total - start.done} left</span>
            </button>
          )}
          {onOpenStats && (
            <button type="button" className="ml-shell__settings" onClick={onOpenStats}>
              <ChartIcon size={18} />
              <span className="ml-shell__settings-label">Stats</span>
            </button>
          )}
          <button type="button" className="ml-shell__settings" onClick={onOpenSettings} data-tour="people">
            <GearIcon size={18} />
            <span className="ml-shell__settings-label">Settings</span>
            <Avatars members={members} />
          </button>
        </aside>
        <main className="ml-shell__main">{children}</main>
      </div>
    </Ctx.Provider>
  )
}

/** Get started's progress: a ring that fills a segment per step done. */
function StartRing({ done, total, size }: { done: number; total: number; size: number }) {
  const r = 9, c = 2 * Math.PI * r
  return (
    <svg className="ml-start-ring" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r={r} className="ml-start-ring__track" />
      <circle cx="12" cy="12" r={r} className="ml-start-ring__fill"
        strokeDasharray={`${(c * done) / Math.max(total, 1)} ${c}`} transform="rotate(-90 12 12)" />
    </svg>
  )
}

/** The phone's frame: the Matlist row, the sliding title strip, and the page that swipes. */
function PhoneShell({ tab, onTab, members, onOpenSettings, onOpenStats, start, panelId, children }: {
  tab: Tab
  onTab: (t: Tab) => void
  members: Member[]
  onOpenSettings: () => void
  onOpenStats?: () => void
  start?: { done: number; total: number; onOpen: () => void }
  panelId: string
  children: ReactNode
}) {
  const index = Math.max(0, PHONE_TABS.findIndex(t => t.value === tab))
  const swipe = useTabSwipe(index, PHONE_TABS.length, i => onTab(PHONE_TABS[i].value))
  const left = start ? start.total - start.done : 0
  // Where each tab sits, so the bar under the open one can slide between them.
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [boxes, setBoxes] = useState<{ lefts: number[]; widths: number[] }>({ lefts: [0, 0, 0, 0], widths: [0, 0, 0, 0] })
  // Measured once laid out, again once Archivo has loaded (it changes the
  // widths) and on a resize.
  useLayoutEffect(() => {
    const measure = () => {
      const lefts = tabRefs.current.map(el => el?.offsetLeft ?? 0)
      const widths = tabRefs.current.map(el => el?.offsetWidth ?? 0)
      setBoxes(prev => (lefts.some((v, i) => v !== prev.lefts[i]) || widths.some((v, i) => v !== prev.widths[i])
        ? { lefts, widths } : prev))
    }
    measure()
    let live = true
    document.fonts?.ready.then(() => { if (live) measure() })
    window.addEventListener('resize', measure)
    return () => { live = false; window.removeEventListener('resize', measure) }
  }, [])

  function onKey(e: KeyboardEvent, i: number) {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const to = (i + step + PHONE_TABS.length) % PHONE_TABS.length
    onTab(PHONE_TABS[to].value)
    tabRefs.current[to]?.focus()
  }

  // The top stays put while the page scrolls; what sticks or scrolls under it
  // (Plan's day strip, a day jumped to) reads its height from --ml-shell-top.
  const shellRef = useRef<HTMLDivElement>(null)
  const topRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const top = topRef.current
    if (!top || typeof ResizeObserver === 'undefined') return
    const set = () => shellRef.current?.style.setProperty('--ml-shell-top', `${top.offsetHeight}px`)
    const ro = new ResizeObserver(set)
    ro.observe(top)
    set()
    return () => ro.disconnect()
  }, [])

  return (
    <div className="ml-shell-phone" ref={shellRef}>
      <div className="ml-shell-phone__top" ref={topRef}>
        <header className="ml-shell-phone__header">
          <Wordmark size={24} />
          <span className="ml-shell-phone__actions">
            {start ? (
              <button type="button" className="ml-stats-btn ml-start-btn" onClick={onOpenStats ?? start.onOpen} data-tour="get-started"
                aria-label={`Get started, ${left} of ${start.total} left${onOpenStats ? ', and Stats' : ''}`}>
                <StartRing done={start.done} total={start.total} size={36} />
                {onOpenStats && <span className="ml-start-btn__icon" aria-hidden="true"><ChartIcon size={16} /></span>}
                <span className={`ml-start-btn__left ml-num${onOpenStats ? ' ml-start-btn__left--badge' : ''}`} aria-hidden="true">{left}</span>
              </button>
            ) : onOpenStats && (
              <button type="button" className="ml-stats-btn" aria-label="Stats" onClick={onOpenStats}>
                <ChartIcon size={18} />
              </button>
            )}
            <SettingsPill members={members} onClick={onOpenSettings} compact />
          </span>
        </header>
        <div role="tablist" aria-label="Sections" className="ml-tabs">
          {PHONE_TABS.map((t, i) => (
            <button key={t.value} ref={el => { tabRefs.current[i] = el }} type="button" role="tab" data-tour={`tab-${t.value}`}
              aria-selected={i === index} aria-controls={panelId} tabIndex={i === index ? 0 : -1}
              className={`ml-tabs__tab${i === index ? ' is-selected' : ''}`}
              onClick={() => onTab(t.value)} onKeyDown={e => onKey(e, i)}>
              {t.label}
            </button>
          ))}
          <span aria-hidden="true" className={`ml-tabs__bar${swipe.phase === 'drag' ? ' is-dragging' : ''}`}
            style={{
              transform: `translateX(${stripX(boxes.lefts, index, swipe.progress)}px)`,
              width: stripX(boxes.widths, index, swipe.progress),
            }} />
        </div>
      </div>
      <div role="tabpanel" aria-label={PHONE_TABS[index].label} id={panelId} {...swipe.bind}
        className={`ml-pager${swipe.phase !== 'idle' ? ` is-moving is-${swipe.phase}` : ''}`}
        style={{ '--ml-page-x': `${swipe.dx}px` } as CSSProperties}>
        {children}
      </div>
    </div>
  )
}
